import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Save as SaveIcon } from '@styled-icons/boxicons-solid/Save';
import { toast } from 'shared/toast';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useMediaQuery,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';
import PaginationWrapper from 'components/Pagination';

import useAlert from 'hooks/useAlert';
import useDebouncedEffect from 'hooks/useDebouncedEffect';

import { Budget } from 'types/budget';

import BulkUpdatePanel from './BulkUpdatePanel';
import FiltersPanel from './FiltersPanel';
import InputsTable from './InputsTable';
import useBulkEditInputs from './useBulkEditInputs';

type ModalBulkEditInputsProps = ModalRootProps & {
  data: {
    budgetId: number;
    budgetBases?: Budget['bases'];
  };
};

const modalMargin = 100;
const modalMobileFix = 80;

const ModalBulkEditInputs: React.FC<ModalBulkEditInputsProps> = ({
  data,
  onConfirm,
  handleClose,
  ...restProps
}) => {
  const [isMobile] = useMediaQuery('(max-width: 728px)');

  const { openAlert } = useAlert();

  const budgetBases = useMemo(() => {
    return data.budgetBases || [];
  }, [data.budgetBases]);

  const [keyword, setKeyword] = useState('');
  const [selectedBudgetBaseId, setSelectedBudgetBaseId] = useState<
    number | null
  >(null);
  const [selectedClassificationType, setSelectedClassificationType] = useState<
    string | null
  >(null);
  const [selectedUnitMeasureId, setSelectedUnitMeasureId] = useState<
    number | null
  >(null);

  const selectedBudgetBase = useMemo(() => {
    if (!selectedBudgetBaseId) {
      return null;
    }

    return budgetBases.find((base) => base.id === selectedBudgetBaseId) || null;
  }, [budgetBases, selectedBudgetBaseId]);

  const selectedVersionId = selectedBudgetBase?.version.id || null;
  const selectedVersionDescription =
    selectedBudgetBase?.version.description || '';

  const shouldSkipKeywordSearchOnMountRef = useRef(true);
  const selectedVersionIdRef = useRef<number | null>(selectedVersionId);
  const selectedClassificationTypeRef = useRef<string | null>(
    selectedClassificationType,
  );
  const selectedUnitMeasureIdRef = useRef<number | null>(selectedUnitMeasureId);
  selectedVersionIdRef.current = selectedVersionId;
  selectedClassificationTypeRef.current = selectedClassificationType;
  selectedUnitMeasureIdRef.current = selectedUnitMeasureId;

  const {
    inputs,
    status,
    page,
    perPage,
    lastPage,
    selectedIds,
    editedValues,
    modifiedIds,
    invalidModifiedIds,
    hasUnsavedChanges,
    hasValidChanges,
    isSaving,
    loadErrorMessage,
    saveErrorMessage,
    setPage,
    setPerPage,
    updateSort,
    applyFilters,
    reload,
    toggleSelection,
    toggleSelectAllCurrentPage,
    clearSelection,
    editUnitPrice,
    undoItemChanges,
    undoSelectedChanges,
    applyFixedPriceToSelection,
    applyPercentageToSelection,
    saveChanges,
  } = useBulkEditInputs({
    budgetId: data.budgetId,
  });

  const selectedCount = selectedIds.length;

  const handleSearch = useCallback(() => {
    applyFilters({
      keyword,
      versionId: selectedVersionId,
      classificationType: selectedClassificationType,
      unitMeasureId: selectedUnitMeasureId,
    });
  }, [
    applyFilters,
    keyword,
    selectedVersionId,
    selectedClassificationType,
    selectedUnitMeasureId,
  ]);

  useDebouncedEffect(
    () => {
      if (shouldSkipKeywordSearchOnMountRef.current) {
        shouldSkipKeywordSearchOnMountRef.current = false;
        return;
      }

      applyFilters({
        keyword,
        versionId: selectedVersionIdRef.current,
        classificationType: selectedClassificationTypeRef.current,
        unitMeasureId: selectedUnitMeasureIdRef.current,
      });
    },
    400,
    [applyFilters, keyword],
  );

  const handleAttemptClose = useCallback(() => {
    if (!hasUnsavedChanges) {
      handleClose();
      return;
    }

    const modifiedItemsDescription =
      modifiedIds.length === 1
        ? '1 insumo alterado será perdido ao sair.'
        : `${modifiedIds.length} insumos alterados serão perdidos ao sair.`;

    openAlert({
      type: 'warning',
      title: 'Alterações não salvas',
      description: `Você possui alterações não salvas. ${modifiedItemsDescription}`,
      buttons: ['Cancelar', 'Sair sem salvar'],
      onConfirm: () => {
        handleClose();
      },
    });
  }, [handleClose, hasUnsavedChanges, modifiedIds.length, openAlert]);

  const handleSave = useCallback(async () => {
    const saved = await saveChanges();

    if (!saved) {
      return;
    }

    toast({
      description: 'Insumos atualizados com sucesso!',
      status: 'success',
    });

    if (onConfirm) {
      onConfirm();
    }

    handleClose();
  }, [handleClose, onConfirm, saveChanges]);

  const handleClearSelection = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  return (
    <Modal
      {...restProps}
      scrollBehavior="inside"
      closeOnEsc
      onClose={handleAttemptClose}
    >
      <ModalOverlay />

      <ModalContent
        sx={{
          width: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxWidth: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          height: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
          maxHeight: `calc(100% - ${
            isMobile ? modalMargin - modalMobileFix : modalMargin
          }px)`,
        }}
      >
        <ModalHeader>Editar insumos do orçamento</ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          {saveErrorMessage && (
            <Alert status="error" mb={4} alignItems="flex-start">
              <AlertIcon />

              <Box>
                <AlertTitle>Erro ao salvar alterações</AlertTitle>
                <AlertDescription>{saveErrorMessage}</AlertDescription>
              </Box>

              <Button
                ml="auto"
                size="sm"
                variant="outline"
                onClick={handleSave}
                isDisabled={isSaving}
              >
                Tentar novamente
              </Button>
            </Alert>
          )}

          <FiltersPanel
            budgetBases={budgetBases}
            selectedBudgetBaseId={selectedBudgetBaseId}
            selectedClassificationType={selectedClassificationType}
            selectedUnitMeasureId={selectedUnitMeasureId}
            selectedVersionDescription={selectedVersionDescription}
            isLoading={status === 'loading'}
            onChangeBudgetBase={setSelectedBudgetBaseId}
            onChangeClassificationType={setSelectedClassificationType}
            onChangeUnitMeasureId={setSelectedUnitMeasureId}
            onSearch={handleSearch}
          />

          <Flex
            mb={3}
            justifyContent="space-between"
            alignItems={{ base: 'stretch', md: 'center' }}
            flexDirection={{ base: 'column', md: 'row' }}
            gridGap={3}
          >
            <HStack spacing={2}>
              <HStack>
                <Text fontSize="smaller">Por página:</Text>

                <Select
                  size="md"
                  width="auto"
                  value={perPage}
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                  }}
                >
                  {[5, 10, 25, 50, 100].map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Select>
              </HStack>
            </HStack>

            <Input
              size="md"
              width={{ base: '100%', md: '540px' }}
              ml={{ base: 0, md: 'auto' }}
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="Buscar por código ou descrição"
            />
          </Flex>

          {status === 'error' && (
            <Alert status="error" mb={3} alignItems="flex-start">
              <AlertIcon />

              <Box>
                <AlertTitle>Erro ao carregar insumos</AlertTitle>
                <AlertDescription>
                  {loadErrorMessage || 'Houve um erro ao carregar os insumos.'}
                </AlertDescription>
              </Box>

              <Button ml="auto" size="sm" variant="outline" onClick={reload}>
                Tentar novamente
              </Button>
            </Alert>
          )}

          {status === 'success' && selectedCount > 0 && (
            <BulkUpdatePanel
              selectedCount={selectedCount}
              onApplyFixedPrice={applyFixedPriceToSelection}
              onApplyPercentage={applyPercentageToSelection}
              onClearSelection={handleClearSelection}
              onUndoSelectedChanges={undoSelectedChanges}
            />
          )}

          <InputsTable
            data={inputs}
            selectedIds={selectedIds}
            editedValues={editedValues}
            invalidModifiedIds={invalidModifiedIds}
            loading={status === 'loading'}
            onToggleSelection={toggleSelection}
            onToggleSelectAllCurrentPage={toggleSelectAllCurrentPage}
            onUpdateSort={updateSort}
            onEditUnitPrice={editUnitPrice}
            onUndoItemChanges={undoItemChanges}
          />

          {status !== 'loading' && status !== 'empty' && (
            <>
              <Text mt={3} textAlign="right" fontSize="sm" color="gray.600">
                Página {page} de {lastPage}
              </Text>

              <PaginationWrapper
                lastPage={lastPage}
                forcePage={page - 1}
                onPaginate={(selectedPage) => {
                  setPage(selectedPage);
                }}
              />
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack justifyContent="space-between" width="100%">
            <Text
              fontSize="sm"
              color={invalidModifiedIds.length ? 'red.500' : 'gray.600'}
            >
              {invalidModifiedIds.length > 0
                ? 'Existem alterações inválidas. Corrija os valores para salvar.'
                : `${modifiedIds.length} insumos alterados`}
            </Text>

            <HStack>
              <Button variant="ghost" onClick={handleAttemptClose}>
                Cancelar
              </Button>

              <Button
                colorScheme="green"
                leftIcon={<Icon as={SaveIcon} />}
                isLoading={isSaving}
                isDisabled={!hasValidChanges || isSaving}
                onClick={handleSave}
              >
                Salvar alterações
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalBulkEditInputs;
